import React, { useState, useEffect } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/twmege"; // Utility function for conditional classNames
import ReactDOM from "react-dom";
import { Box, Button } from "@dxp-feed/ui";

// Define the toast variants with Tailwind classes
const toastVariants = cva(
  "font-bold rounded-md shadow-md py-[14px] px-4 gap-4 flex items-center z-50", // Shared base classes
  {
    variants: {
      position: {
        top: "fixed top-4 left-1/2 -translate-x-1/2",
        bottom: "fixed bottom-4 left-1/2 -translate-x-1/2",
        topLeft: "fixed top-4 left-4",
        topRight: "fixed top-4 right-4",
        bottomLeft: "fixed bottom-4 left-4",
        bottomRight: "fixed bottom-4 right-4",
      },
      bgColor: {
        success: "bg-primary100 text-brandBlack",
        error: "bg-[#e16645] text-brandWhite",
        info: "bg-blue-500 text-brandWhite",
        warning: "bg-[#25a9ef] text-brandWhite",
      },
    },
    defaultVariants: {
      position: "top", // Default position
      bgColor: "info", // Default background color
    },
  }
);

export type ToastProps = VariantProps<typeof toastVariants> & {
  message: React.ReactNode; // Allow HTML or components in message
  autoCloseDuration?: number; // Optionally define auto-close duration
  onClose: (e?: any) => void; // Function to notify the parent to close the toast
  className?: string; // Custom className for additional styling
  closeButton?: React.ReactNode; // Custom close button (optional)
  isGlobalSearch?: boolean;
  setDropDown?: (value: boolean) => void;
};

export const CustomToast: React.FC<ToastProps> = ({
  position,
  bgColor,
  message,
  autoCloseDuration = 3000, // Default duration is 3 seconds
  onClose,
  className,
  isGlobalSearch,
  setDropDown,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, autoCloseDuration);
    if (timer && setDropDown) {
      setDropDown(true);
    }
    return () => clearTimeout(timer);
  }, [autoCloseDuration, onClose]);

  if (!isVisible) return null;
  // const toasts = document.querySelectorAll('.custom-toast-popup');
  // toasts.forEach((toast, index) => {
  //   if (index !== toasts.length - 1) {
  //     toast.classList.add('hidden');
  //   } else {
  //     toast.classList.remove('hidden');
  //   }
  // });

  return ReactDOM.createPortal(
    <Box
      as="div"
      id="custom-toast-id"
      className={`${cn(toastVariants({ position, bgColor }), className)} z-[999999] custom-toast-popup max-sm:!w-full max-sm:!max-w-[90%]`}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      onClick={(e: any) => {
        e.stopPropagation();
        if (isGlobalSearch && setDropDown) {
          setDropDown(true);
        }
      }}
    >
      {message && <div className="flex-1">{message}</div>}

      <Button
        variant="icon"
        onClick={(e) => onClose(e)}
        className="z-[99] text-brandBlack text-[14px] p-0"
      >
        <Box as="span" className="demo-icon icon-close"></Box>
      </Button>
    </Box>,
    document.body
  );
};
